<template>
  <div
    class="ticket-selection__bookables"
    :class="[orderType, isUpdate && 'is-update']"
  >
    <h4 v-if="!isUpdate" class="ticket-selection__bookables-title">
      {{
        orderType === 'add-ons'
          ? $t('landingpage.checkout-page.confirmation.additional-bookings')
          : $t('landingpage.checkout-page.ticket-selection.bookables.title')
      }}
    </h4>

    <div class="ticket-selection__bookables-intro">
      <p>
        {{
          isUpdate
            ? $t('common.bookable_groups')
            : orderType === 'add-ons'
              ? $t(
                  'landingpage.checkout-page.ticket-selection.add-ons.bookables.intro',
                )
              : $t('landingpage.checkout-page.ticket-selection.bookables.intro')
        }}
      </p>
    </div>

    <div class="ticket-selection__bookables-list">
      <template v-for="addOnGroup in addOnGroups" :key="addOnGroup.id">
        <template v-for="addOn in addOnGroup.addons" :key="addOn.id">
          <label
            :for="`ticket-selection-bookable-item-`"
            class="ticket-selection__bookable-item"
            :class="[
              addOn.items.length && 'ticket-selection__bookable-item--active',
            ]"
          >
            <div class="ticket-selection__bookable-content">
              <div class="ticket-selection__bookable-details">
                <p class="ticket-selection__bookable-name">
                  {{ addOn.name[activeLocale] }}

                  <span
                    v-if="
                      addOn.options?.length &&
                      addOn.options.every((o) => o.price?.gross === 0) &&
                      !isSoldOut(addOn)
                    "
                    class="ticket-selection__bookable-free whitespace-nowrap"
                  >
                    {{ $t('landingpage.checkout-page.ticket-selection.free') }}
                  </span>

                  <span
                    v-if="isSoldOut(addOn)"
                    class="ticket-selection__bookable-sold-out"
                  >
                    {{
                      $t('landingpage.checkout-page.add-on-selection.sold-out')
                    }}
                  </span>
                </p>

                <p
                  v-if="addOn.description[activeLocale]"
                  class="ticket-selection__bookable-description"
                  v-text="addOn.description[activeLocale]"
                />

                <!-- Fixed: Quantity based -->
                <template v-if="addOn.availability_type === 'fixed'">
                  <p
                    class="ticket-selection__bookable-availability"
                    :class="
                      addOn.options[0]?.quantity?.remaining === 0 &&
                      'ticket-selection__bookable-availability--none'
                    "
                  >
                    {{
                      $t(
                        'landingpage.checkout-page.ticket-selection.available',
                        {
                          count: addOn.options[0]?.quantity?.remaining || 0,
                        },
                      )
                    }}
                  </p>

                  <div class="ticket-selection__bookable-selector-container">
                    <div
                      v-if="addOn.items?.length"
                      class="ticket-selection__bookable-selected"
                    >
                      <FontAwesomeIcon :icon="['far', 'check']" />
                      <span class="ticket-selection__bookable-selected-text">
                        {{
                          $t(
                            'landingpage.checkout-page.ticket-selection.bookables.selected-count',
                            {
                              selected: addOn.items[0]?.quantity,
                            },
                          )
                        }}
                      </span>
                      <button
                        @click="
                          () => {
                            addOn.quantitySelected = 0
                            removeSelected(addOn)
                            addOn.items = []
                          }
                        "
                      >
                        <FontAwesomeIcon
                          :icon="['far', 'xmark']"
                          class="text-base"
                        />
                      </button>
                    </div>

                    <p
                      v-else-if="
                        maxSelectableFor(addOn) === 0 && !isSoldOut(addOn)
                      "
                      class="text-sm text-gray-500 italic"
                    >
                      {{
                        $t(
                          'landingpage.checkout-page.ticket-selection.bookables.max-booking-reached',
                        )
                      }}
                    </p>

                    <div v-else class="flex items-center gap-5">
                      <QuantitySelector
                        :key="`qs-${addOn.id}-${maxSelectableFor(addOn)}`"
                        class="ticket-selection__bookable-selector"
                        :max="maxSelectableFor(addOn)"
                        :value="addOn.quantitySelected"
                        @change="(value) => (addOn.quantitySelected = value)"
                      />
                      <button
                        class="btn min-w-0! w-auto! flex! items-center! gap-2! p-2! text-sm"
                        :class="!addOn.quantitySelected && 'btn-disabled'"
                        :disabled="!addOn.quantitySelected"
                        @click="
                          addOnQuantityChanged(addOn.quantitySelected, addOn)
                        "
                      >
                        <FontAwesomeIcon
                          :icon="['far', 'plus']"
                          class="text-sm"
                        />
                        {{ $t('common.add') }}
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Flexible (Slot-based) -->
                <template
                  v-if="
                    addOn.items?.length &&
                    addOn.availability_type === 'flexible'
                  "
                >
                  <div
                    class="ticket-selection__bookable-selected-options-wrapper"
                  >
                    <div
                      v-for="item in addOn.items"
                      :key="item.id"
                      class="ticket-selection__bookable-selected-option"
                    >
                      <FontAwesomeIcon :icon="['far', 'check']" />

                      <span>
                        {{
                          `${formatDate(item?.starts_at, eventSettings?.timezone)} • ${formatTime(item?.starts_at, eventSettings?.timezone, false)} - ${formatTime(item?.ends_at, eventSettings?.timezone, false)} (${Math.round((new Date(item?.ends_at) - new Date(item?.starts_at)) / 60000)} min)`
                        }}
                      </span>
                      <button
                        type="button"
                        @click="
                          removeSelectedSlot({
                            slot_id: item.id,
                            ...addOn,
                          })
                        "
                      >
                        <FontAwesomeIcon :icon="['far', 'xmark']" />
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Nights (date-based) - consolidated display -->
                <template
                  v-if="
                    addOn.items?.length && addOn.availability_type === 'nights'
                  "
                >
                  <div
                    class="ticket-selection__bookable-selected-options-wrapper"
                  >
                    <div class="ticket-selection__bookable-selected-option">
                      <FontAwesomeIcon :icon="['far', 'check']" />

                      <template
                        v-for="range in [getNightsDateRange(addOn.items)]"
                        :key="range"
                      >
                        <span>
                          {{ formatDateWithWeekday(range.first?.starts_at) }}
                          -
                          {{ formatDateWithWeekday(range.last?.ends_at) }}
                          ({{ addOn.items.length }}
                          {{
                            addOn.items.length === 1
                              ? $t('common.night')
                              : $t('common.nights')
                          }})
                        </span>
                      </template>
                      <button type="button" @click="removeSelected(addOn)">
                        <FontAwesomeIcon :icon="['far', 'xmark']" />
                      </button>
                    </div>
                  </div>
                </template>
              </div>

              <div
                v-if="
                  addOn.availability_type === 'fixed' &&
                  addOn.options[0]?.price?.gross > 0
                "
                class="ticket-selection__bookable-price"
              >
                <p class="ticket-selection__bookable-price-label">
                  {{
                    $t(
                      'landingpage.checkout-page.ticket-selection.bookables.price',
                    )
                  }}
                </p>
                <p class="ticket-selection__bookable-price-value">
                  {{
                    formatCurrencyLeft(
                      addOn.options[0]?.price?.gross || 0,
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      orderDetails?.currency,
                    )
                  }}
                </p>
              </div>

              <div
                v-if="addOn.availability_type !== 'fixed'"
                class="flex flex-col gap-3"
              >
                <div
                  v-if="getLowestPriceOption(addOn.options)?.price?.gross > 0"
                  class="ticket-selection__bookable-price"
                >
                  <p class="ticket-selection__bookable-price-label">
                    {{
                      $t(
                        hasCustomAvailability(addOn)
                          ? 'landingpage.checkout-page.ticket-selection.bookables.from_price'
                          : 'landingpage.checkout-page.ticket-selection.bookables.price',
                      )
                    }}
                  </p>
                  <p class="ticket-selection__bookable-price-value">
                    {{
                      formatCurrencyLeft(
                        getLowestPriceOption(addOn.options)?.price?.gross || 0,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                        orderDetails?.currency,
                      )
                    }}
                  </p>
                </div>

                <p
                  v-if="
                    !isSoldOut(addOn) &&
                    ['flexible', 'nights'].includes(addOn.availability_type) &&
                    !hasFutureAvailability(addOn) &&
                    !isMaxBookingReached(addOn)
                  "
                  class="text-sm text-gray-500 italic"
                >
                  {{
                    $t(
                      'landingpage.checkout-page.ticket-selection.bookables.no-dates-available',
                    )
                  }}
                </p>

                <div
                  v-if="canSelectOptions(addOn)"
                  class="ticket-selection__bookable-btn-container"
                >
                  <button
                    type="button"
                    class="ticket-selection__bookable-btn-options btn btn-outline-primary"
                    @click="
                      () => {
                        selectedAddOn = addOn
                        addOn.showOptionsModal = true
                      }
                    "
                  >
                    <FontAwesomeIcon :icon="['far', 'calendar']" />
                    {{
                      addOn.availability_type === 'nights'
                        ? $t(
                            'landingpage.checkout-page.ticket-selection.bookables.select-dates',
                          )
                        : $t(
                            'landingpage.checkout-page.ticket-selection.bookables.select-option',
                          )
                    }}
                  </button>

                  <NightBasedOptionsModal
                    v-if="
                      addOn.availability_type === 'nights' &&
                      addOn.showOptionsModal
                    "
                    :add-on="addOn"
                    @close="addOn.showOptionsModal = false"
                    @confirm="
                      async (option) => {
                        addOn.showOptionsModal = false
                        const payload = {
                          id: addOn.id,
                          quantity: 1,
                          start_at: option.start_at,
                          end_at: option.end_at,
                        }
                        if (props.assignAddOn) {
                          const success = await props.assignAddOn(payload)
                          if (!success) return
                        } else {
                          addOnOptionSelected(option)
                        }
                        addOn.items.push({
                          id: addOn.id,
                          starts_at: option?.start_at,
                          ends_at: option?.end_at,
                          quantity: 1,
                        })
                      }
                    "
                  />

                  <SlotBasedOptionsModal
                    v-if="
                      addOn.availability_type === 'flexible' &&
                      addOn.showOptionsModal
                    "
                    :add-on="addOn"
                    @close="addOn.showOptionsModal = false"
                    @confirm="
                      async (options) => {
                        addOn.showOptionsModal = false
                        for (let i = 0; i < options.length; i++) {
                          const option = options[i]

                          if (props.assignAddOn) {
                            const isLast = i === options.length - 1
                            const success = await props.assignAddOn(
                              {
                                id: addOn.id,
                                quantity: 1,
                                start_at: option.start_at,
                                end_at: option.end_at,
                              },
                              { skipRefetch: !isLast },
                            )
                            if (!success) return
                          }

                          addOn.items.push({
                            id: `${addOn.id}-${option?.start_at}-${option?.end_at}`,
                            starts_at: option?.start_at,
                            ends_at: option?.end_at,
                            quantity: 1,
                          })
                        }
                      }
                    "
                  />
                </div>

                <p
                  v-else-if="isMaxBookingReached(addOn)"
                  class="text-sm text-gray-500 italic"
                >
                  {{
                    $t(
                      'landingpage.checkout-page.ticket-selection.bookables.max-booking-reached',
                    )
                  }}
                </p>
              </div>
            </div>
          </label>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, computed } from 'vue'
import { intlFormat } from 'date-fns'
import QuantitySelector from '@/Components/common/QuantitySelector.vue'
import NightBasedOptionsModal from '@/Components/LandingPage/Checkout/NightBasedOptionsModal.vue'
import SlotBasedOptionsModal from '@/Components/LandingPage/Checkout/SlotBasedOptionsModal.vue'
import { formatCurrencyLeft } from '@/composables/currency'
import { formatDate, formatTime } from '@/composables/date'
import { useCurrentLocaleCode } from '@/composables/locale'

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  isUpdate: {
    type: Boolean,
    default: () => false,
  },
  assignAddOn: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits([
  'add-on-quantity-changed',
  'add-on-option-selected',
  'selected',
  'removed',
])

const addOnGroups = computed(() => props.items)

const selectedAddOn = ref(null)
const orderDetails = inject('orderDetails')
const activeLocale = inject('activeLocale')
const orderType = inject('orderType')
const eventSettings = inject('eventSettings')

const addOnQuantityChanged = (quantity, addOn) => {
  emit('selected', { quantity, ...addOn })
}

const addOnOptionSelected = (option) => {
  emit('selected', {
    quantity: 1,
    start_at: option.start_at,
    end_at: option.end_at,
    ...selectedAddOn.value,
  })
}

const removeSelected = (addOn) => {
  emit('removed', { ...addOn, reservation_id: addOn.items[0].id })
}
const removeSelectedSlot = (addOn) => {
  emit('removed', { ...addOn, reservation_id: addOn.slot_id })
}

const formatDateWithWeekday = (date) => {
  return intlFormat(
    new Date(date),
    {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
    { locale: useCurrentLocaleCode() },
  )
}

const getNightsSortedItems = (items) => {
  return [...items].sort(
    (a, b) => new Date(a.starts_at) - new Date(b.starts_at),
  )
}

const getNightsDateRange = (items) => {
  if (!items.length) {
    return { first: undefined, last: undefined }
  }
  const sorted = getNightsSortedItems(items)
  return { first: sorted[0], last: sorted[sorted.length - 1] }
}

const getLowestPriceOption = (options) => {
  if (!options?.length) return null
  return options.reduce((lowest, option) => {
    if (!lowest || option.price?.gross < lowest.price?.gross) {
      return option
    }
    return lowest
  }, null)
}

const isSoldOut = (addOn) => {
  const allOptionsSoldOut =
    !addOn.options?.length ||
    addOn.options.every((o) => (o.quantity?.remaining ?? 0) === 0)

  if (addOn.availability_type === 'nights') {
    const perNight = addOn.availability_per_night
    return (
      !perNight ||
      Object.values(perNight).every((n) => (n.capacity?.remaining ?? 0) === 0)
    )
  }

  return allOptionsSoldOut
}

const hasCustomAvailability = (addOn) => {
  const options = addOn.options
  if (!options || options.length === 0) {
    return false
  }
  const firstPrice = options[0].price?.gross
  return options.some((option) => option.price?.gross !== firstPrice)
}

const getBookingTotalForAddOn = (addOn) => {
  if (!orderDetails.value?.line_items) return 0
  return orderDetails.value.line_items
    .filter(({ product_type }) => product_type.includes('UniversalBookable'))
    .reduce((total, lineItem) => {
      const matching =
        lineItem.bookable_reservations?.filter(
          (r) => r.product?.id === addOn.id,
        ) ?? []
      return total + matching.reduce((sum, r) => sum + (r.quantity ?? 1), 0)
    }, 0)
}

const isMaxBookingReached = (addOn) => {
  if (addOn.max_quantity_per_booking == null) return false
  return getBookingTotalForAddOn(addOn) >= addOn.max_quantity_per_booking
}

/**
 * How many units of a quantity-based (fixed) add-on this guest may still add: the smaller of the
 * remaining contingent and whatever the per-guest / per-booking caps leave over.
 *
 * Unlike SlotBasedOptionsModal, this reads sold_to_guest / sold_to_booking off the option rather than
 * scanning orderDetails.line_items. Those two come from the same query AddAddonToOrderAction validates
 * against, and they also count reservations that never went through this cart (e.g. Suite bulk-assigned
 * ones), which a line-item scan would miss.
 */
const maxSelectableFor = (addOn) => {
  const option = addOn.options?.[0]
  if (!option) return 0

  const perGuestRemaining =
    addOn.max_quantity_per_guest == null
      ? Infinity
      : Math.max(
          0,
          addOn.max_quantity_per_guest - (option.quantity?.sold_to_guest ?? 0),
        )

  const perBookingRemaining =
    addOn.max_quantity_per_booking == null
      ? Infinity
      : Math.max(
          0,
          addOn.max_quantity_per_booking -
            (option.quantity?.sold_to_booking ?? 0),
        )

  return Math.min(
    option.quantity?.remaining ?? 0,
    perGuestRemaining,
    perBookingRemaining,
  )
}

const canSelectOptions = (addOn) => {
  if (isSoldOut(addOn) || !hasFutureAvailability(addOn)) return false
  if (isMaxBookingReached(addOn)) return false

  if (['fixed', 'nights'].includes(addOn.availability_type)) {
    return !addOn.items?.length
  }

  if (addOn.availability_type === 'flexible') {
    const withinLimit =
      addOn.max_quantity_per_guest == null ||
      (addOn.items || []).length < addOn.max_quantity_per_guest

    const hasAvailableSlot = addOn.options?.some(
      (o) =>
        o.quantity.remaining > 0 &&
        !(addOn.items || []).some(
          (i) => i.starts_at === o.start_at && i.ends_at === o.end_at,
        ),
    )

    return withinLimit && hasAvailableSlot
  }

  return false
}

const hasFutureAvailability = (addOn) => {
  const now = new Date()
  if (addOn.availability_type === 'nights') {
    const perNight = addOn.availability_per_night
    if (!perNight?.length) return false
    const soldOutDates = perNight
      .filter((n) => (n.capacity?.remaining ?? 0) === 0)
      .map((n) => n.date)
    return addOn.options?.some((option) => {
      const startDate = option.start_at?.substring(0, 10)
      if (!startDate || startDate < now.toISOString().substring(0, 10)) {
        return false
      }
      const nights = perNight.filter(
        (n) => n.date >= startDate && n.date < option.end_at?.substring(0, 10),
      )
      return (
        nights.length > 0 && nights.every((n) => !soldOutDates.includes(n.date))
      )
    })
  }
  if (addOn.availability_type === 'flexible') {
    return addOn.options?.some(
      (o) => new Date(o.start_at) >= now && o.quantity.remaining > 0,
    )
  }
  return true
}
</script>
